import { useState } from 'react';
import {
  FRONT_REGIONS, BACK_REGIONS,
  BODY_OUTLINE_FRONT_MALE, BODY_OUTLINE_FRONT_FEMALE,
  BODY_OUTLINE_BACK_MALE, BODY_OUTLINE_BACK_FEMALE,
  type RegionDef, type BodyOutlinePath,
} from './body-map-data';

export interface BodyAnnotation {
  regionId: string;
  nota: string;
}

// Kept for backwards compatibility
export interface BodyRegion {
  id: string;
  label: string;
  shape: 'rect' | 'ellipse' | 'polygon';
  props: Record<string, number | string>;
}

export interface BodyMapProps {
  view: 'front' | 'back';
  gender: 'masculino' | 'femenino';
  annotations: BodyAnnotation[];
  selectedRegion: string | null;
  onRegionClick: (regionId: string, label: string) => void;
  canEdit: boolean;
}

// ─── Colour helpers ───────────────────────────────────────────────────────────

function regionFill(id: string, selected: string | null, hovered: string | null, annotated: boolean): string {
  if (id === selected) return '#2563eb';
  if (annotated)       return '#3b82f6';
  if (id === hovered)  return '#93c5fd';
  return '#d1d5db';
}

function regionOpacity(id: string, selected: string | null, hovered: string | null, annotated: boolean): number {
  if (id === selected) return 0.9;
  if (annotated)       return 0.65;
  if (id === hovered)  return 0.7;
  return 0.35;
}

// ─── BodyMap ──────────────────────────────────────────────────────────────────

export function BodyMap({ view, gender, annotations, selectedRegion, onRegionClick, canEdit }: BodyMapProps) {
  const [hovered, setHovered] = useState<string | null>(null);

  const regions: RegionDef[]       = view === 'front' ? FRONT_REGIONS : BACK_REGIONS;
  const outlineMale: BodyOutlinePath[]   = view === 'front' ? BODY_OUTLINE_FRONT_MALE   : BODY_OUTLINE_BACK_MALE;
  const outlineFemale: BodyOutlinePath[] = view === 'front' ? BODY_OUTLINE_FRONT_FEMALE : BODY_OUTLINE_BACK_FEMALE;
  const outlinePaths = gender === 'masculino' ? outlineMale : outlineFemale;

  const annotationMap = Object.fromEntries(annotations.map(a => [a.regionId, a.nota]));
  const hoveredRegion = hovered ? regions.find(r => r.id === hovered) : null;

  return (
    <div className="flex flex-col items-center gap-2">
      <svg
        viewBox="0 0 676.49 1203.49"
        className="w-full max-w-[240px]"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Hover label */}
        {hoveredRegion && (
          <g pointerEvents="none">
            <rect
              x={338 - hoveredRegion.label.length * 4.8}
              y={10}
              width={hoveredRegion.label.length * 9.6}
              height={26}
              rx={6}
              fill="#1e293b"
              opacity={0.85}
            />
            <text
              x={338} y={27}
              textAnchor="middle"
              fontSize={16}
              fill="white"
              fontFamily="system-ui, sans-serif"
              fontWeight="600"
            >
              {hoveredRegion.label}
            </text>
          </g>
        )}

        {/* Body outline */}
        <g
          fill="none"
          stroke="#cbd5e1"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.75}
          pointerEvents="none"
        >
          {outlinePaths.map((p, i) =>
            p.type === 'path'
              ? <path key={i} d={p.d} />
              : <line key={i} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} />
          )}
        </g>

        {/* Interactive muscle regions */}
        {regions.map(r => {
          const paths = gender === 'masculino' ? r.pathsMale : r.pathsFemale;
          if (!paths.length) return null;
          const annotated = !!annotationMap[r.id];
          const selected  = r.id === selectedRegion;
          const fill    = regionFill(r.id, selectedRegion, hovered, annotated);
          const opacity = regionOpacity(r.id, selectedRegion, hovered, annotated);
          return (
            <g
              key={r.id}
              style={{ cursor: canEdit ? 'pointer' : 'default' }}
              onClick={() => canEdit && onRegionClick(r.id, r.label)}
              onMouseEnter={() => setHovered(r.id)}
              onMouseLeave={() => setHovered(null)}
            >
              {paths.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill={fill}
                  opacity={opacity}
                  stroke={selected ? '#1d4ed8' : annotated ? '#2563eb' : 'none'}
                  strokeWidth={selected ? 2 : 1.5}
                />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] text-text-secondary">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-300 inline-block" /> Sin nota
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-400 inline-block" /> Con nota
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-600 inline-block" /> Seleccionada
        </span>
      </div>
    </div>
  );
}
